import type {ReactElement} from 'react';
import React from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Type} from '@Components/button';
import {NavRightOptionsSkeleton} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-right-options-skeleton';
import BaseNavOption from '@Components/poster-editor/components/poster-editor-nav-bar/components/base-nav-option/base-nav-option';
import {PublishRepublishButton} from '@Components/poster-editor/components/poster-editor-nav-bar/components/publish-republish-button';
import {ElementToHide} from '@Components/poster-editor/poster-editor.types';
import {openMessageModal} from '@Modals/message-modal';
import {openPosterEditorNavMoreOptionsModal} from '@Modals/poster-editor-nav-more-options-modal';
import {NavRightSideSkeletonSize} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-right-options-skeleton/nav-right-options-skeleton.types';
import {NavRightSideOptionTypes} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-right-options/nav-right-options.types';
import {AutoSaveOption} from '@Components/poster-editor/components/poster-editor-nav-bar/components/auto-save-option/auto-save-option';
import {useAppSelector} from '@/hooks';
import {useShowContextualSkeletons} from '@Hooks/poster-editor/useShowContextualSkeletons';
import {NavSaveOption} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-save-option/nav-save-option';
import {EmbeddedEditorCustomButton} from '@Components/poster-editor/components/poster-editor-nav-bar/components/embedded-editor-custom-button/embedded-editor-custom-button';
import {NavDownloadOption} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-download-option/nav-download-option';
import {NavShareOption} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-share-option/nav-share-option';
import {openPremiumPricingModal} from '@Components/modals/premium-pricing-modal/premium-pricing-modal.library';
import {COOKIE_EXPIRY_30_MINUTES, setCookie} from '@Utils/cookie.util';
import {NEW_SUBSCRIPTION_START_SOURCE_COOKIE, NewSubscriptionStartSource} from '@Libraries/ga-events';

interface NavRightOptionsProps {
  className?: string;
  showHelpOption?: boolean;
  saveOptionType?: NavRightSideOptionTypes;
  upgradeOptionType?: NavRightSideOptionTypes;
  collaborateOptionType?: NavRightSideOptionTypes;
  publishOptionType?: NavRightSideOptionTypes;
  downloadOptionType?: NavRightSideOptionTypes;
  showMoreOptionsDropdown?: boolean;
  showAutoSaveText?: boolean;
  openExportPanelOnPublishOrDownload?: boolean;
  skeletonSize?: NavRightSideSkeletonSize;
}

export function NavRightOptions({
  className = '',
  saveOptionType = NavRightSideOptionTypes.BUTTON,
  upgradeOptionType = NavRightSideOptionTypes.BUTTON,
  collaborateOptionType = NavRightSideOptionTypes.BUTTON,
  publishOptionType = NavRightSideOptionTypes.BUTTON,
  downloadOptionType = NavRightSideOptionTypes.BUTTON,
  showHelpOption = false,
  showMoreOptionsDropdown = false,
  showAutoSaveText = false,
  openExportPanelOnPublishOrDownload = false,
  skeletonSize = NavRightSideSkeletonSize.LARGE,
}: NavRightOptionsProps): ReactElement<any> {
  const showSkeletons = useShowContextualSkeletons();
  const isEmbeddedExportMode = useAppSelector((state) => {
    return state.posterEditor.isEmbeddedExportMode;
  });
  const isInitialized = useAppSelector((state) => {
    return state.posterEditor.posterObject?.isInitialized;
  });
  const hasUserEnabledAutoSave = useAppSelector((state) => {
    return state.userPreferences.isAutosaveEnabled;
  });
  const currentUserId = useAppSelector((state) => {
    return state.posterEditor.currentUserId;
  });

  const showUpgradeOption = (): ReactElement<any> | null => {
    if ((currentUserId && Number(window.PMW.getUserPremiumLevel()) !== 0) || window.posterEditor?.config?.elementsToHide?.includes(ElementToHide.UPGRADE)) {
      return null;
    }

    return (
      <BaseNavOption
        id="nav-upgrade-option"
        icon="icon-crown"
        text={window.i18next.t('pmwjs_upgrade')}
        iconType={IconType.PREMIUM}
        buttonType={Type.PREMIUM}
        optionType={upgradeOptionType}
        onClickHandler={(): void => {
          if (window.PMW.isEmbedded()) {
            openMessageModal({
              title: window.i18next.t('pmwjs_if_upgraded_modal_title'),
              text: window.i18next.t('pmwjs_if_upgraded_modal_desc'),
              ctaButton: {
                text: window.i18next.t('pmwjs_refresh'),
                onClick: (): void => {
                  window.location.reload();
                },
              },
            });
          }

          setCookie(NEW_SUBSCRIPTION_START_SOURCE_COOKIE, NewSubscriptionStartSource.EDITOR_UPGRADE_BUTTON, COOKIE_EXPIRY_30_MINUTES);
          openPremiumPricingModal(false);
        }}
        hasMarginRight
      />
    );
  };

  const showHelpIcon = (): ReactElement<any> | null => {
    if (showHelpOption) {
      return (
        <Icon
          onClick={(): void => {
            window.open('https://support.postermywall.com/hc/en-us/articles/360058560052?utm_source=editor&utm_medium=nav');
          }}
          className="spacing-m-r-2"
          icon="icon-help"
          shape={IconShape.SQUARE}
          size={IconSize.SIZE_ICON_20}
          type={IconType.DARK_GHOST}
        />
      );
    }
    return null;
  };

  const showAutoSaveOption = (): ReactElement<any> | null => {
    if (isEmbeddedExportMode || !window.posterEditor?.whiteboard?.savePoster.isAutoSaveEnabled() || !hasUserEnabledAutoSave) {
      return null;
    }

    return <AutoSaveOption showLine={showHelpOption} showText={showAutoSaveText} showVersionHistoryOnHover={true} />;
  };

  const showSaveOption = (): ReactElement<any> | null => {
    return <NavSaveOption saveOptionType={saveOptionType} />;
  };

  const showCollaborateOption = (): ReactElement<any> | null => {
    if (isEmbeddedExportMode) {
      return null;
    }
    return <NavShareOption collaborateOptionType={collaborateOptionType} isLoading={!isInitialized} />;
  };

  const showEmbeddedEditorCustomButton = (): ReactElement<any> | null => {
    return <EmbeddedEditorCustomButton saveOptionType={saveOptionType} />;
  };

  const showDownloadOptionNew = (): ReactElement | null => {
    if (window.posterEditor?.config?.elementsToHide?.includes(ElementToHide.DOWNLOAD) || isEmbeddedExportMode) {
      return null;
    }
    return <NavDownloadOption downloadOptionType={downloadOptionType} />;
  };

  const showExportOption = (): ReactElement<any> | null => {
    if (!isEmbeddedExportMode) {
      return null;
    }

    return (
      <BaseNavOption
        id="nav-export-option"
        icon="icon-share"
        text={window.i18next.t('pmwjs_export')}
        optionType={NavRightSideOptionTypes.BUTTON}
        iconType={IconType.GHOST}
        buttonType={Type.GHOST}
        onClickHandler={(): void => {
          void window.posterEditor?.whiteboard?.exportDesign();
        }}
        hasMarginRight
      />
    );
  };

  const showPublishOption = (): ReactElement<any> | null => {
    if (isEmbeddedExportMode) {
      return null;
    }

    return <PublishRepublishButton showPanelOnClick={openExportPanelOnPublishOrDownload} type={publishOptionType} />;
  };

  const onClickMoreOptions = (): void => {
    openPosterEditorNavMoreOptionsModal();
  };

  const moreOptionsDropdown = (): ReactElement<any> | null => {
    if (showMoreOptionsDropdown) {
      return <Icon icon="icon-more-h" onClick={onClickMoreOptions} className="spacing-m-r-2" size={IconSize.SIZE_ICON_20} type={IconType.DARK_GHOST} shape={IconShape.SQUARE} />;
    }
    return null;
  };

  return (
    <div className={`js-blurred-during-onboarding flex-row-align-center ${className} _full-height`}>
      {showSkeletons ? (
        <NavRightOptionsSkeleton size={skeletonSize} />
      ) : (
        <>
          {showHelpIcon()}
          {showAutoSaveOption()}
          {showUpgradeOption()}
          {showSaveOption()}
          {moreOptionsDropdown()}
          {showCollaborateOption()}
          <div className="js-publish-download-options flexbox">
            {showDownloadOptionNew()}
            {showPublishOption()}
          </div>
          {showExportOption()}
          {showEmbeddedEditorCustomButton()}
        </>
      )}
    </div>
  );
}
